import type { GradeTask } from "@/app/gradingQueries";
import type { Comment, SubmissionBrief, SubmissionView, Thread } from "@/app/reviewQueries";
import type { Task } from "@/kernel/runner";
import { count } from "@/lib/plural";
import type { Status } from "@/ui/StatusChip";

/* S7 / M2: всё, что видно на экране, выводится из SubmissionView — треды,
   перечень правок, счётчики, история. Статичных списков нет (спека §2.3, §2.9). */

export type SubStatus = SubmissionBrief["status"];

/** Статусы сдачи → чипы A0 (CLAUDE.md: на проверке — warn, нужны правки — err, принято — ok). */
export const STATUS_CHIP: Record<SubStatus, Status> = { on_review: "in_review", changes_requested: "needs_changes", accepted: "accepted" };
export const STATUS_LABEL: Record<SubStatus, string> = { on_review: "на проверке", changes_requested: "нужны правки", accepted: "принято" };
export const STATUS_TONE: Record<SubStatus, "warn" | "err" | "ok"> = { on_review: "warn", changes_requested: "err", accepted: "ok" };

/** SLA ментора (настройка школы, открытый вопрос CLAUDE.md). */
export const SLA_H = 24;

export const fileName = (assignment: string) => (assignment.endsWith(".ipynb") ? assignment : `${assignment}.ipynb`);

const two = (n: number) => String(n).padStart(2, "0");
/** «12.09, 18:20» — как в макете S7. */
export function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return `${two(d.getDate())}.${two(d.getMonth() + 1)}, ${two(d.getHours())}:${two(d.getMinutes())}`;
}

export const waitHours = (from: string, now = Date.now()) => Math.max(0, (now - Date.parse(from)) / 3_600_000);

/** Номер задачи комментария: task_n, иначе хвост cell_id («task-7», «checks-11»). */
export function taskOf(c: { task_n?: number; cell_id: string }): number | undefined {
  if (c.task_n) return c.task_n;
  const m = /-(\d+)$/.exec(c.cell_id);
  return m ? Number(m[1]) : undefined;
}

/** «ячейка 7 · задача 7» (спека §7: показываем номер задачи; id ячейки — в подсказке). */
export function cellWhere(c: { task_n?: number; cell_id: string }): string {
  const n = taskOf(c);
  return n ? `ячейка ${n} · задача ${n}` : `ячейка ${c.cell_id}`;
}

export type CellRow = {
  n: number;
  cellId: string;
  title: string;
  status: GradeTask["status"] | "none";
  kind: string;
  assertLine: string;
  traceback: string[];
  threads: Thread[];
};

/** Ячейки версии: по задаче прогона — строка со статусом и трейсбеком, треды
 *  подвешиваются по номеру задачи. Треды вне задач — отдельными строками в конце. */
export function cellRows(view: SubmissionView, tasks: Task[] = []): CellRow[] {
  const byN = new Map<number, CellRow>();
  const graded = [...(view.run?.result?.tasks ?? [])].sort((a, b) => a.n - b.n);
  for (const t of graded) {
    byN.set(t.n, { n: t.n, cellId: `task-${t.n}`, title: tasks.find((x) => x.n === t.n)?.title ?? "", status: t.status, kind: t.error_kind, assertLine: t.assert_line, traceback: t.traceback, threads: [] });
  }
  if (byN.size === 0) for (const t of tasks) byN.set(t.n, { n: t.n, cellId: t.cell_id, title: t.title, status: "none", kind: "", assertLine: "", traceback: [], threads: [] });
  const extra: CellRow[] = [];
  for (const th of view.threads) {
    const n = taskOf(th.root);
    const row = n ? byN.get(n) : undefined;
    if (row) row.threads.push(th);
    else {
      const e = extra.find((x) => x.cellId === th.root.cell_id);
      if (e) e.threads.push(th);
      else extra.push({ n: 0, cellId: th.root.cell_id, title: "", status: "none", kind: "", assertLine: "", traceback: [], threads: [th] });
    }
  }
  return [...byN.values(), ...extra];
}

/** Комментарии ментора = корни тредов; ответы студента в счётчик не входят (§2.9). */
export const rootCount = (view: Pick<SubmissionView, "threads">) => view.threads.length;
export const replyCount = (view: Pick<SubmissionView, "threads">) => view.threads.reduce((a, t) => a + t.replies.length, 0);
export const commentsLabel = (n: number) => count(n, "комментарий", "комментария", "комментариев");

/** Сводка причин по корневым комментариям: «пропуски и типы данных ×2». */
export function reasonSummary(threads: Thread[]): { label: string; n: number }[] {
  const m = new Map<string, number>();
  for (const t of threads) if (t.root.reason) m.set(t.root.reason.label, (m.get(t.root.reason.label) ?? 0) + 1);
  return [...m].map(([label, n]) => ({ label, n })).sort((a, b) => b.n - a.n || a.label.localeCompare(b.label, "ru"));
}

export const isStudent = (c: Comment, view: Pick<SubmissionView, "student">) => c.author.id === view.student.id;

/** Прогон версии: «11 / 14», клетки по задачам. */
export function runScore(view: Pick<SubmissionView, "run">): { passed: number; total: number; cells: GradeTask["status"][] } | undefined {
  const r = view.run;
  if (!r || !r.result) return undefined;
  const total = r.total || r.result.tasks.length;
  return { passed: r.passed, total, cells: Array.from({ length: total }, (_, i) => r.result!.tasks.find((t) => t.n === i + 1)?.status ?? "idle") };
}

export type HistoryEvent = { at: string; text: string; submissionId: string };

/** История (S7): по версиям, новые сверху, внутри версии — по времени:
 *  отправлена (со счётом, если прогон известен) → вердикт (с числом комментариев) → ответы. */
export function historyEvents(all: SubmissionBrief[], current?: SubmissionView, studentName = "студент"): HistoryEvent[] {
  const out: HistoryEvent[] = [];
  const sorted = [...all].sort((a, b) => b.iteration - a.iteration);
  for (const b of sorted) {
    const cur = current && current.id === b.id ? current : undefined;
    const score = cur && runScore(cur);
    out.push({ at: b.submitted_at, text: `${studentName}: отправлена v${b.iteration}${score ? ` · ${score.passed}/${score.total} автопроверок` : ""}`, submissionId: b.id });
    if (b.verdict_at && b.status !== "on_review") {
      const who = b.reviewer?.name ?? "ментор";
      out.push({ at: b.verdict_at, text: `${who}: «${STATUS_LABEL[b.status]}»${cur ? ` · ${commentsLabel(rootCount(cur))}` : ""}`, submissionId: b.id });
    }
    if (cur) {
      const replies = cur.threads.flatMap((t) => t.replies.map((r) => ({ r, n: taskOf(t.root) }))).sort((a, b) => Date.parse(a.r.created_at) - Date.parse(b.r.created_at));
      for (const { r, n } of replies) out.push({ at: r.created_at, text: `${r.author.name}: ответ в треде ${n ? `задачи ${n}` : `ячейки ${r.cell_id}`}`, submissionId: b.id });
    }
  }
  return out;
}

/** Текущая сдача по умолчанию — последняя (новые сверху). */
export const latestId = (list: SubmissionBrief[] | undefined, chosen?: string) => chosen ?? list?.[0]?.id;
